import React, { useState } from 'react';
import {
  Box,
  Grid,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UploadedFile } from './FileUpload';

interface ImageGalleryProps {
  files: UploadedFile[];
  title?: string;
  onDelete?: (fileId: number) => Promise<void>;
  currentUserId?: number;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ files, title, onDelete, currentUserId }) => {
  const { t } = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);
  
  // Филтрираме само снимките
  const images = files.filter((file) => file.file_type.startsWith('image/'));

  const handleOpen = (index: number) => {
    setSelectedIndex(index);
  };

  const handleClose = () => {
    setSelectedIndex(null);
  };

  const handlePrevious = () => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedIndex !== null && selectedIndex < images.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    } else if (e.key === 'Escape') {
      handleClose();
    }
  };

  const handleDownload = (file: UploadedFile) => {
    window.open(file.url, '_blank');
  };

  const handleDelete = async (fileId: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Не отваряме dialog при кликване на delete
    
    if (!onDelete) return;
    
    if (window.confirm(t('tasks.details.confirm_delete_file', 'Сигурни ли сте, че искате да изтриете този файл?'))) {
      setDeleting(fileId);
      try {
        await onDelete(fileId);
        // Затваряме dialog ако сме го отворили
        if (selectedIndex !== null && images[selectedIndex]?.id === fileId) {
          handleClose();
        }
      } catch (error) {
        console.error('Error deleting file:', error);
      } finally {
        setDeleting(null);
      }
    }
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      
      <Grid container spacing={2}>
        {images.map((image, index) => (
          <Grid item xs={6} sm={4} md={3} key={image.id}>
            <Paper
              sx={{
                position: 'relative',
                paddingTop: '100%',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.05)',
                  '& .overlay': {
                    opacity: 1,
                  },
                },
              }}
              onClick={() => handleOpen(index)}
            >
              <Box
                component="img"
                src={image.thumbnail_url || image.url}
                alt={image.filename}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <Box
                className="overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                }}
              >
                <Typography variant="body2" color="white" textAlign="center" px={1}>
                  {image.filename}
                </Typography>
                
                {/* Delete Button - само за собственика на файла */}
                {onDelete && currentUserId && image.uploaded_by_id === currentUserId && (
                  <IconButton
                    size="small"
                    onClick={(e) => handleDelete(image.id, e)}
                    disabled={deleting === image.id}
                    sx={{
                      mt: 1,
                      color: 'white',
                      backgroundColor: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.dark',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Lightbox Dialog */}
      <Dialog
        open={selectedIndex !== null}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        onKeyDown={handleKeyDown}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent
          sx={{
            position: 'relative',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
            overflow: 'hidden',
          }}
        >
          {selectedIndex !== null && (
            <>
              {/* Close Button */}
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  color: 'white',
                  zIndex: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Download Button */}
              <IconButton
                onClick={() => handleDownload(images[selectedIndex])}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: onDelete && currentUserId && images[selectedIndex].uploaded_by_id === currentUserId ? 128 : 72,
                  color: 'white',
                  zIndex: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <DownloadIcon />
              </IconButton>

              {/* Delete Button - само за собственика на файла */}
              {onDelete && currentUserId && images[selectedIndex].uploaded_by_id === currentUserId && (
                <IconButton
                  onClick={(e) => handleDelete(images[selectedIndex].id, e)}
                  disabled={deleting === images[selectedIndex].id}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 72,
                    color: 'white',
                    zIndex: 1,
                    backgroundColor: 'rgba(255, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 0, 0.7)',
                    },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              )}

              {/* Previous Button */}
              {selectedIndex > 0 && (
                <IconButton
                  onClick={handlePrevious}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <PrevIcon fontSize="large" />
                </IconButton>
              )}

              {/* Next Button */}
              {selectedIndex < images.length - 1 && (
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                  }}
                >
                  <NextIcon fontSize="large" />
                </IconButton>
              )}

              {/* Image */}
              <Box
                component="img"
                src={images[selectedIndex].url}
                alt={images[selectedIndex].filename}
                sx={{
                  maxWidth: '90%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />

              {/* Image Info */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  textAlign: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2">
                  {images[selectedIndex].filename}
                </Typography>
                <Typography variant="caption" color="grey.400">
                  {selectedIndex + 1} / {images.length}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImageGallery;
